import { useEffect, useState, useRef, RefObject } from 'react';

interface UseCarouselOptions {
  /** 走马灯项目的总数 */
  totalItems: number;
  /** 初始显示的索引，默认 0 */
  initialIndex?: number;
  /** 滚轮切换的阈值，默认 8 */
  wheelThreshold?: number;
  /** 滚轮事件的时间窗口（毫秒），默认 250 */
  wheelTimeWindow?: number;
  /** 切换后的处理延迟（毫秒），默认 500 */
  switchDelay?: number;
  /** 是否启用滚轮切换，默认 true */
  enableWheel?: boolean;
  /** 是否启用触摸手势，默认 true */
  enableTouch?: boolean;
  /** 滚轮触发切换时调用；若提供则由调用方控制切换（如先播动画再 goToSlide），不直接 setCurrentIndex */
  onWheelSlideChange?: (newIndex: number) => void;
  /** 是否首尾循环，默认 true；为 false 时在第一张不能继续向左滑、最后一张不能继续向右滑 */
  wrap?: boolean;
}

interface UseCarouselReturn {
  /** 当前索引 */
  currentIndex: number;
  /** 跳转到指定索引 */
  goToSlide: (index: number) => void;
  /** 下一个 */
  next: () => void;
  /** 上一个 */
  prev: () => void;
  /** 走马灯容器的 ref，需要绑定到容器元素 */
  carouselRef: RefObject<HTMLDivElement>;
}

/**
 * 走马灯（轮播）Hook
 * 支持鼠标滚轮和触摸手势切换
 */
export function useCarousel(options: UseCarouselOptions): UseCarouselReturn {
  const {
    totalItems,
    initialIndex = 0,
    wheelThreshold = 8,
    wheelTimeWindow = 250,
    switchDelay = 500,
    enableWheel = true,
    enableTouch = true,
    onWheelSlideChange,
    wrap = true,
  } = options;

  const [currentIndex, setCurrentIndex] = useState(() =>
    Math.max(0, Math.min(initialIndex, totalItems - 1)),
  );
  const carouselRef = useRef<HTMLDivElement>(null);
  const lastWheelTimeRef = useRef(0);
  const lastTriggerTimeRef = useRef(0);
  const isProcessingRef = useRef(false);
  const accumulatedDeltaRef = useRef(0);
  const currentIndexRef = useRef(0);
  currentIndexRef.current = currentIndex;

  /** 触发一次切换后，在此时间内不再响应滚轮切换（避免一次滑动连切多张）；须 >= switchDelay，否则触摸结束时尾随的 wheel 会再次触发 */
  const WHEEL_COOLDOWN_MS = Math.max(500, switchDelay + 150);

  const goToSlide = (index: number) => {
    if (index >= 0 && index < totalItems) {
      setCurrentIndex(index);
    }
  };

  const next = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex < totalItems - 1) {
        return prevIndex + 1;
      }
      return prevIndex;
    });
  };

  const prev = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex > 0) {
        return prevIndex - 1;
      }
      return prevIndex;
    });
  };

  useEffect(() => {
    const wrapperElement = carouselRef.current;
    if (!wrapperElement) return;

    // 处理滚轮事件
    const handleWheel = (e: WheelEvent) => {
      if (!enableWheel) return;

      const deltaX = e.deltaX;
      const deltaY = e.deltaY;
      const isHorizontalScroll = Math.abs(deltaX) > Math.abs(deltaY);

      if (isHorizontalScroll) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isProcessingRef.current) {
        return;
      }

      const now = Date.now();
      if (now - lastTriggerTimeRef.current < WHEEL_COOLDOWN_MS) {
        return;
      }

      if (isHorizontalScroll) {
        const timeSinceLastWheel = now - lastWheelTimeRef.current;
        lastWheelTimeRef.current = now;

        if (timeSinceLastWheel < wheelTimeWindow) {
          accumulatedDeltaRef.current += deltaX;
        } else {
          accumulatedDeltaRef.current = deltaX;
        }

        if (Math.abs(accumulatedDeltaRef.current) >= wheelThreshold) {
          isProcessingRef.current = true;

          const savedDirection = accumulatedDeltaRef.current > 0 ? 1 : -1;

          accumulatedDeltaRef.current = 0;

          const prevIndex = currentIndexRef.current;
          let newIndex = prevIndex;
          // 触控板：向左滑 (deltaX > 0) → 下一张；向右滑 (deltaX < 0) → 上一张
          if (savedDirection > 0) {
            newIndex = prevIndex < totalItems - 1 ? prevIndex + 1 : wrap ? 0 : prevIndex;
          } else {
            newIndex = prevIndex > 0 ? prevIndex - 1 : wrap ? totalItems - 1 : prevIndex;
          }

          lastTriggerTimeRef.current = now;

          if (onWheelSlideChange && newIndex !== prevIndex) {
            onWheelSlideChange(newIndex);
          } else if (!onWheelSlideChange) {
            setCurrentIndex(newIndex);
          }

          setTimeout(() => {
            isProcessingRef.current = false;
            accumulatedDeltaRef.current = 0;
          }, switchDelay);
        }
      }
    };

    // 处理触摸手势事件
    const handleTouchStart = (e: TouchEvent) => {
      if (!enableTouch) return;
      if (e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!enableTouch) return;
      if (e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleGestureStart = (e: Event) => {
      if (!enableTouch) return;
      e.preventDefault();
      e.stopPropagation();
    };

    const handleGestureChange = (e: Event) => {
      if (!enableTouch) return;
      e.preventDefault();
      e.stopPropagation();
    };

    const handleGestureEnd = (e: Event) => {
      if (!enableTouch) return;
      e.preventDefault();
      e.stopPropagation();
    };

    // 在 document 级别阻止手势事件，防止浏览器前进后退
    // 只处理来自 wrapperElement 内部的手势事件
    const handleDocumentGestureStart = (e: Event) => {
      if (!enableTouch) return;
      const target = e.target as Node;
      if (wrapperElement.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleDocumentGestureChange = (e: Event) => {
      if (!enableTouch) return;
      const target = e.target as Node;
      if (wrapperElement.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleDocumentGestureEnd = (e: Event) => {
      if (!enableTouch) return;
      const target = e.target as Node;
      if (wrapperElement.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 绑定事件监听器
    if (enableWheel) {
      wrapperElement.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    }

    if (enableTouch) {
      wrapperElement.addEventListener('touchstart', handleTouchStart, {
        passive: false,
        capture: true,
      });
      wrapperElement.addEventListener('touchmove', handleTouchMove, {
        passive: false,
        capture: true,
      });
      wrapperElement.addEventListener('gesturestart', handleGestureStart, {
        passive: false,
        capture: true,
      });
      wrapperElement.addEventListener('gesturechange', handleGestureChange, {
        passive: false,
        capture: true,
      });
      wrapperElement.addEventListener('gestureend', handleGestureEnd, {
        passive: false,
        capture: true,
      });
      // 在 document 级别也监听手势事件，防止浏览器前进后退
      document.addEventListener('gesturestart', handleDocumentGestureStart, {
        passive: false,
        capture: true,
      });
      document.addEventListener('gesturechange', handleDocumentGestureChange, {
        passive: false,
        capture: true,
      });
      document.addEventListener('gestureend', handleDocumentGestureEnd, {
        passive: false,
        capture: true,
      });
    }

    // 清理事件监听器
    return () => {
      if (enableWheel) {
        wrapperElement.removeEventListener('wheel', handleWheel, { capture: true });
      }
      if (enableTouch) {
        wrapperElement.removeEventListener('touchstart', handleTouchStart, { capture: true });
        wrapperElement.removeEventListener('touchmove', handleTouchMove, { capture: true });
        wrapperElement.removeEventListener('gesturestart', handleGestureStart, { capture: true });
        wrapperElement.removeEventListener('gesturechange', handleGestureChange, { capture: true });
        wrapperElement.removeEventListener('gestureend', handleGestureEnd, { capture: true });
        document.removeEventListener('gesturestart', handleDocumentGestureStart, { capture: true });
        document.removeEventListener('gesturechange', handleDocumentGestureChange, {
          capture: true,
        });
        document.removeEventListener('gestureend', handleDocumentGestureEnd, { capture: true });
      }
    };
  }, [
    totalItems,
    wheelThreshold,
    wheelTimeWindow,
    switchDelay,
    enableWheel,
    enableTouch,
    onWheelSlideChange,
    wrap,
  ]);

  return {
    currentIndex,
    goToSlide,
    next,
    prev,
    carouselRef,
  };
}
